/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and custom performance metrics
 */

import { errorLogger, ErrorSeverity } from "@/services/errorLogger.service";

interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  navigationType?: string;
}

/**
 * Get rating based on metric value and threshold
 */
function getRating(
  value: number,
  good: number,
  poor: number
): "good" | "needs-improvement" | "poor" {
  if (value <= good) return "good";
  if (value <= poor) return "needs-improvement";
  return "poor";
}

/**
 * Report metric to analytics and logging
 */
function reportMetric(metric: PerformanceMetric) {
  // Log to console in development
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[Performance] ${metric.name}:`, {
      value: metric.value.toFixed(2),
      rating: metric.rating,
    });
  }

  // Log to error service for monitoring
  if (metric.rating === "poor") {
    errorLogger.logError(
      new Error(
        `Poor ${metric.name}: ${metric.value.toFixed(2)}${metric.name === "CLS" ? "" : "ms"}`
      ),
      ErrorSeverity.WARNING
    );
  }

  // Send to analytics if available (Google Analytics, etc.)
  if (typeof window !== "undefined" && "gtag" in window) {
    (window as any).gtag("event", metric.name, {
      value: Math.round(metric.value),
      metric_rating: metric.rating,
      event_category: "Web Vitals",
    });
  }
}

/**
 * Measure Largest Contentful Paint (LCP)
 */
export function measureLCP() {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;

      const metric: PerformanceMetric = {
        name: "LCP",
        value: lastEntry.renderTime || lastEntry.loadTime,
        rating: getRating(lastEntry.renderTime || lastEntry.loadTime, 2500, 4000),
      };

      reportMetric(metric);
    });

    observer.observe({ entryTypes: ["largest-contentful-paint"] });
  } catch {
    // PerformanceObserver not supported or failed
  }
}

/**
 * Measure First Input Delay (FID)
 */
export function measureFID() {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry: any) => {
        const metric: PerformanceMetric = {
          name: "FID",
          value: entry.processingStart - entry.startTime,
          rating: getRating(entry.processingStart - entry.startTime, 100, 300),
        };

        reportMetric(metric);
      });
    });

    observer.observe({ entryTypes: ["first-input"] });
  } catch {
    // PerformanceObserver not supported or failed
  }
}

/**
 * Measure Cumulative Layout Shift (CLS)
 */
export function measureCLS() {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

  try {
    let clsValue = 0;
    const clsEntries: any[] = [];

    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          clsEntries.push(entry);
        }
      });
    });

    observer.observe({ entryTypes: ["layout-shift"] });

    // Report CLS on page visibility change
    const reportCLS = () => {
      const metric: PerformanceMetric = {
        name: "CLS",
        value: clsValue,
        rating: getRating(clsValue, 0.1, 0.25),
      };

      reportMetric(metric);
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        reportCLS();
      }
    });
  } catch {
    // PerformanceObserver not supported or failed
  }
}

/**
 * Measure First Contentful Paint (FCP)
 */
export function measureFCP() {
  if (typeof window === "undefined" || !("PerformanceObserver" in window)) return;

  try {
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.name === "first-contentful-paint") {
          const metric: PerformanceMetric = {
            name: "FCP",
            value: entry.startTime,
            rating: getRating(entry.startTime, 1800, 3000),
          };

          reportMetric(metric);
        }
      });
    });

    observer.observe({ entryTypes: ["paint"] });
  } catch {
    // PerformanceObserver not supported or failed
  }
}

/**
 * Measure Time to First Byte (TTFB)
 */
export function measureTTFB() {
  if (typeof window === "undefined" || !("performance" in window)) return;

  try {
    const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming;

    if (navigation) {
      const metric: PerformanceMetric = {
        name: "TTFB",
        value: navigation.responseStart - navigation.requestStart,
        rating: getRating(navigation.responseStart - navigation.requestStart, 600, 1500),
        navigationType: navigation.type,
      };

      reportMetric(metric);
    }
  } catch {
    // Performance API not supported or failed
  }
}

/**
 * Initialize all performance monitoring
 */
export function initPerformanceMonitoring() {
  if (typeof window === "undefined") return;

  // Measure core web vitals
  measureLCP();
  measureFID();
  measureCLS();
  measureFCP();
  measureTTFB();

  // Log initial page load performance
  window.addEventListener("load", () => {
    setTimeout(() => {
      const perfData = performance.timing;
      const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
      const connectTime = perfData.responseEnd - perfData.requestStart;
      const renderTime = perfData.domComplete - perfData.domLoading;

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log("[Performance] Page Load Metrics:", {
          pageLoadTime: `${pageLoadTime}ms`,
          connectTime: `${connectTime}ms`,
          renderTime: `${renderTime}ms`,
        });
      }
    }, 0);
  });
}

/**
 * Custom hook to measure component render time
 */
export function measureComponentRender(componentName: string, renderTime: number) {
  if (renderTime > 16) {
    // Longer than one frame (16.67ms at 60fps)
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[Performance] Slow render in ${componentName}: ${renderTime.toFixed(2)}ms`);
    }
  }
}
